import React, { useState } from "react";
import {
  Grid,
  FormControl,
  Typography,
  IconButton,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
} from "@mui/material";
import Uppy from '@uppy/core';
import { DragDrop, StatusBar } from '@uppy/react';
import XHRUpload from '@uppy/xhr-upload';
import '@uppy/core/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import '@uppy/status-bar/dist/style.css';
import '@uppy/informer/dist/style.css';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/GetAppSharp";
import moment from "moment";
import parse from "html-react-parser";
import AlertDialog from "../../components/alert/alertComponent";
import { getInitials } from "../../utils/common";
import AttachmentService from "../../services/api/attachmentService";
import { UserIcon, DownloadLink, AttachmentDesc } from "./styles";
import RichTextEditor from "../RichTextEditor";
import CustomizedSnackbars from "../CustomizedSnackbars";
import { getUniqueFileName } from "../../utils/common/attachment"

const CardAttachments = ({
  data,
  allAttachments,
  updateAllAttachments,
  boardData,
}) => {
  // state declarations
  const [files, setFiles] = useState([]);

  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [attachmentDescription, setAttachmentDescription] = useState(null);

  const [deleteAttachmentAlert, setDeleteAttachmentAlert] = useState(false);
  const [attachmentData, setAttachmentData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [uppyInstance, setUppyInstance] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMessage, setErrorToastMessage] = useState('');

  React.useEffect(() => {
    const uppy = new Uppy({
      autoProceed: true,
      allowMultipleUploads: true,
      restrictions: {
        maxFileSize: boardData?.fileSizeDisplay * 1024 * 1024,
      }
    })
      .use(XHRUpload, {
        endpoint: '/api/v2/card/attachments',
        method: 'POST',
        fieldName: 'file',
        formData: true
      })
      .on('files-added', (_files) => {
        setErrorMessages([]);
      })
      .on('file-added', (file) => {
        const uniqueName = getUniqueFileName(file.name, allAttachments);
        if (uniqueName !== file.name) {
          const newFile = new File([file.data], uniqueName, { type: file.type });
          uppy.removeFile(file.id);
          uppy.addFile({
            name: uniqueName,
            type: file.type,
            data: newFile,
            meta: { cardId: data.id }
          });
        } else {
          uppy.setFileMeta(file.id, { cardId: data.id });
        }
      })
      .on('restriction-failed', (file, error) => {
        setErrorMessages(prev => {
          const newErrors = [...prev, `${file.name}: ${error.message}`];
          setErrorToastMessage(newErrors);
          setShowErrorToast(true);
          return newErrors;
        });
      })
      .on('complete', (result) => {
        if (result.successful.length > 0) {
          updateAttachmentEntry(null, data.id);
        }
      })

    setUppyInstance(uppy);

    return () => {
      if (uppy) {
        uppy.close();
      }
    };
  }, [data.id, boardData.fileSizeDisplay, allAttachments]);

  // Auto-close toast after 10 seconds
  React.useEffect(() => {
    if (showErrorToast) {
      const timer = setTimeout(() => setShowErrorToast(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [showErrorToast]);

  const updateAttachmentEntry = (file, cardId) => {
    AttachmentService.getAllAttachmentsForCard(cardId).then(
      (resAttachments) => {
        updateAllAttachments(resAttachments.data);
        setFiles([]);
      }
    );
  };

  // Download Attachment
  const downloadAttachment = (attachment) => {
    AttachmentService.downloadAttachment(attachment).then((response) => {
      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = attachment.attachment_name;
      document.body.appendChild(link);
      link.click();
    });
  };

  // Open Dialog Box
  const attachmentAlertOpen = (item) => {
    setDeleteAttachmentAlert(true);
    setAttachmentData(item);
  };
  const attachmentAlertClose = () => {
    setDeleteAttachmentAlert(false);
  };
  const confirmDeleteAttachment = () => {
    deleteAttachment(attachmentData);
  };

  const deleteAttachment = (item) => {
    setLoader(true);
    AttachmentService.deleteAttachment(item).then((response) => {
      if (response.data === "successfully deleted") {
        AttachmentService.deleteAttachmentEntry({
          item,
        }).then((responseEntry) => {
          if (responseEntry.data === "Deleted successfully") {
            AttachmentService.getAllAttachmentsForCard(data.id).then(
              (resAttachments) => {
                setLoader(false);
                updateAllAttachments(resAttachments.data);
                setDeleteAttachmentAlert(false);
              }
            );
          }
        });
      }
    });
  };

  const openDescriptionDialog = (event, item) => {
    setDescriptionOpen(true);
    setAttachmentData(item);
    setAttachmentDescription(item.description ? item.description : null);
    event.preventDefault();
  };

  const updateAttachmentDescription = (value) => {
    setAttachmentDescription(value);
  };

  const saveAttachmentDescription = () => {
    const item = {
      id: attachmentData.id,
      old_description: attachmentData.description,
      description: attachmentDescription,
      card_id: attachmentData.cardid,
    };
    AttachmentService.updateDescription({
      item,
    }).then((response) => {
      AttachmentService.getAllAttachmentsForCard(data.id).then(
        (attachmentResponse) => {
          updateAllAttachments(attachmentResponse.data);
          setDescriptionOpen(false);
        }
      );
    });
  };

  const parseHtml = (htmlMessage) => {
    let returndata = "null";
    if (htmlMessage) {
      returndata = parse(htmlMessage);
    }
    return returndata;
  };

  const downloadAllAttachments = () => {
    AttachmentService.downloadAllAttachments(data.id).then((response) => {
      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      // eslint-disable-next-line linebreak-style
      const str = `Attached_file_for_${data.id}_${Date.now()}.zip`;
      link.download = str;
      document.body.appendChild(link);
      link.click();
      // let fileName = response.headers.filename;
      // boardService
      //   .deleteFileafterdone(fileName)
      //   .then((response1) => {
      //     console.log('Deleting the own zip file done. ')
      //   })
    })
  };

  return (
    <>
      <Grid container spacing={1} pb={1}>
        <Grid item xs={12}>
          <FormControl variant="standard" fullWidth>
            <Grid container justifyContent={'space-between'} alignItems={'center'}>
              <Grid item>
                <Typography fontWeight={'bold'}>Attachments</Typography>
              </Grid>
              <Grid item>
                <Button
                  title="Clicking this will download all attachments as ZIP file."
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={downloadAllAttachments}
                  variant="text"
                  color="primary"
                >
                  Download all files
                </Button>
              </Grid>
            </Grid>
            {boardData.userPermission &&
              boardData.userPermission.permission.includes("card_edit") ? (
              <>
                {uppyInstance && (
                  <>
                    <DragDrop
                      uppy={uppyInstance}
                      note={`Drag files here or click to browse. ${boardData.fileSizeDisplay ? `Max size: ${boardData.fileSizeDisplay} MB` : ''}`}
                    />
                    <StatusBar
                      uppy={uppyInstance}
                      hideUploadButton={true}
                      hideRetryButton={true}
                      hidePauseResumeButton={true}
                      hideCancelButton={true}
                      showProgressDetails={true}
                    />
                  </>
                )}
              </>
            ) : null}
          </FormControl>
        </Grid>
      </Grid>

      <Card
        variant="outlined"
        sx={{
          p: 2,
          borderWidth: '1.5px',
          borderColor: "#adadad",
          borderRadius: '7px'
        }}
      >
        {allAttachments.length > 0 ? (
          allAttachments.map((item, i) => (
            <Grid container spacing={1} key={item.id} alignItems={'center'}>
              <Grid
                item
                xs={2}
                sm={1}
                md={1}
                lg={0.75}
                className="userIcon"
              >
                {item.username && item.username.profile_picture ? (
                  <img
                    src={item.username.profile_picture}
                    height={30}
                    width={30}
                    alt=""
                    style={{
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  <UserIcon>
                    <div>
                      {getInitials(
                        item.username && item.username.lname
                          ? `${item.username.lname} ${item.username.fname.split(" ")[0]
                          }`
                          : item.author.toString()
                      )}
                    </div>
                  </UserIcon>
                )}
              </Grid>
              <Grid item xs={8} sm={9} md={9} lg={9.25}>
                <b
                  style={{
                    fontSize: 14,
                  }}
                >
                  <DownloadLink onClick={() => downloadAttachment(item)}>
                    {item.attachment_name}
                  </DownloadLink>
                </b>{" "}
                attached by{" "}
                <b>
                  {item.username && item.username.dname
                    ? item.username.dname
                    : item.author}
                </b>{" "}
                on {moment(item.created).format("DD-MMM-YYYY hh:mm A")}
                {/* {(boardData.userPermission && boardData.userPermission.permission.includes('card_edit'))?( */}
                <Typography>
                  {!item.description ? (
                    boardData.userPermission &&
                      boardData.userPermission.permission.includes(
                        "card_edit"
                      ) ? (
                      <Link
                        href="#"
                        onClick={(event) => openDescriptionDialog(event, item)}
                      >
                        Add Description
                      </Link>
                    ) : null
                  ) : (
                    <AttachmentDesc>
                      <span className="heading">
                        <b>Description:</b>
                        {boardData.userPermission &&
                          boardData.userPermission.permission.includes(
                            "card_edit"
                          ) ? (
                          <span className="editIcon">
                            <EditIcon
                              color="primary"
                              onClick={(event) =>
                                openDescriptionDialog(event, item)
                              }
                            />
                          </span>
                        ) : null}
                      </span>
                      <div className="ql-editor">
                        {" "}
                        {parseHtml(item.description)}{" "}
                      </div>
                    </AttachmentDesc>
                  )}
                </Typography>
                {/* ):null} */}
              </Grid>
              <Grid item xs={1} sm={1} md={1}>
                {boardData.userPermission &&
                  boardData.userPermission.permission.includes("card_edit") ? (
                  <IconButton
                    aria-label="delete"
                    style={{
                      padding: 0,
                      marginLeft: 10,
                    }}
                    onClick={() => attachmentAlertOpen(item)}
                    size="large"
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                ) : null}
              </Grid>
            </Grid>
          ))
        ) : (
          <span>No attachment found</span>
        )}
      </Card>
      {/** Delete Confirmation popup - Start */}
      <AlertDialog
        handleClose={attachmentAlertClose}
        alertopen={deleteAttachmentAlert}
        message="Are you sure want to delete the attachment ?"
        key="alertdeleteattachment"
        title="Alert"
        confirmbutton
        confirmFunc={() => confirmDeleteAttachment()}
        loader={loader}
      />
      {/** Delete Confirmation popup - End */}
      {/** */}
      <Dialog aria-labelledby="simple-dialog-title" open={descriptionOpen}>
        <DialogTitle id="alert-dialog-title" color="black">
          Add Description
        </DialogTitle>
        <DialogContent>
          <div>
            <RichTextEditor
              editorValue={attachmentDescription}
              handleEditorChange={updateAttachmentDescription}
              showTablesModule="false"
              showTextModule="true"
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={saveAttachmentDescription}
            color="primary"
            variant="outlined"
            id="save_changes"
          >
            Save Changes
          </Button>
          <Button
            onClick={() => setDescriptionOpen(false)}
            variant="outlined"
            id="cancel"
            color="default_button"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      {/** */}
      {showErrorToast && (
        <CustomizedSnackbars
          messageType="error"
          message={Array.isArray(errorToastMessage) ?
            errorToastMessage.map((error, index) => (
              <div key={index}>{error}</div>
            )) : errorToastMessage
          }
        />
      )}
    </>
  );
};

export default CardAttachments;
